import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useEffect, useState } from 'react';
import { CreateCategoryRequest, CategoryType } from '@/types/model';
import { categoryTypes } from '@/constants';
import { useTranslation } from '@/hooks';
import { Translation } from '@/locales';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Toggle from '@/components/ui/Toggle';
import Button from '@/components/ui/Button';
import ImageNotAvailable from '@/assets/image-not-available.png';
import ImageUrlUploadField from '@/components/ui/ImageUrlUploadField';
import { MediaSource } from '@/types/media';

const createCategorySchema = (t: Translation) => z.object({
  description: z
    .string()
    .min(2, t.validation.minLength(2))
    .max(100, t.validation.maxLength(100)),
  englishDescription: z
    .string()
    .min(2, t.validation.minLength(2))
    .max(100, t.validation.maxLength(100)),
  type: z.enum(categoryTypes, {
    message: t.validation.required
  }),
  imageUrl: z
    .string()
    .url(t.validation.urlFormat)
    .optional()
    .or(z.literal('')),
  active: z.boolean(),
  adultContent: z.boolean(),
  sportsContent: z.boolean()
});

type CategoryFormData = z.infer<ReturnType<typeof createCategorySchema>>;

interface CategoryFormProps {
  onSuccess?: (data: CreateCategoryRequest) => void;
  onCancel?: () => void;
  initialData?: Partial<CreateCategoryRequest>;
  isEdit?: boolean;
  isSubmitting?: boolean;
  lockedType?: CategoryType;
}

const CategoryForm = ({
  onSuccess,
  onCancel,
  initialData,
  isEdit = false,
  isSubmitting = false,
  lockedType
}: CategoryFormProps) => {
  const { t } = useTranslation();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset
  } = useForm<CategoryFormData>({
    resolver: zodResolver(createCategorySchema(t)),
    defaultValues: {
      description: initialData?.description || '',
      englishDescription: initialData?.englishDescription || '',
      type: lockedType || initialData?.type || 'MOVIE',
      imageUrl: initialData?.imageUrl || '',
      active: initialData?.active ?? true,
      adultContent: initialData?.adultContent ?? false,
      sportsContent: initialData?.sportsContent ?? false
    }
  });

  useEffect(() => {
    if (lockedType) {
      setValue('type', lockedType);
    }
  }, [lockedType, setValue]);

  const typeOptions = categoryTypes.map(type => ({
    value: type,
    label: t.categories.types[type as keyof typeof t.categories.types] || type
  }));


  const onSubmit = async (data: CategoryFormData) => {
    setSubmitError(null);

    try {
      await onSuccess?.(data);
      if (!isEdit) {
        reset();
      }
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : t.errors.unexpectedError);
    }
  };

  const activeValue = watch('active');
  const adultContentValue = watch('adultContent');
  const sportsContentValue = watch('sportsContent');
  const imageUrlValue = watch('imageUrl');

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900">
          {isEdit ? t.categories.editTitle : t.categories.createTitle}
        </h2>
        <p className="text-gray-600 mt-1">
          {isEdit ? t.categories.editDescription : t.categories.createDescription}
        </p>
      </div>

      {submitError && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">{submitError}</p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="flex flex-col md:flex-row gap-6 items-start">
          <div className="flex-1 space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Input
                label={t.categories.fields.description}
                placeholder={t.categories.placeholders.description}
                error={errors.description?.message}
                {...register('description')}
              />

              <Input
                label={t.categories.fields.englishDescription}
                placeholder={t.categories.placeholders.englishDescription}
                error={errors.englishDescription?.message}
                {...register('englishDescription')}
              />
            </div>

            <ImageUrlUploadField
              label={t.categories.fields.imageUrl}
              placeholder={t.categories.placeholders.imageUrl}
              value={imageUrlValue || ''}
              onChange={(url) => setValue('imageUrl', url, { shouldValidate: true, shouldDirty: true })}
              error={errors.imageUrl?.message}
              disabled={isSubmitting}
              source={MediaSource.CATEGORY_IMAGE}
              showPreview={false}
            />

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Select
                label={t.categories.fields.type}
                options={typeOptions}
                placeholder={t.categories.placeholders.selectType}
                error={errors.type?.message}
                disabled={Boolean(lockedType)}
                {...register('type')}
              />

              <div className="flex items-center">
                <Toggle
                  label={t.categories.fields.active}
                  checked={activeValue}
                  onChange={(checked) => setValue('active', checked)}
                  disabled={isSubmitting}
                />
              </div>
              <div className="flex items-center">
                <Toggle
                  label={t.categories.fields.adultContent}
                  checked={adultContentValue}
                  onChange={(checked) => setValue('adultContent', checked)}
                  disabled={isSubmitting}
                />
              </div>

              <div className="flex items-center">
                <Toggle
                  label={t.categories.fields.sportsContent}
                  checked={sportsContentValue}
                  onChange={(checked) => setValue('sportsContent', checked)}
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>

          <div className="w-full md:w-48 flex justify-start">
            {imageUrlValue ? (
              <img
                src={imageUrlValue}
                alt={t.categories.fields.imageUrl}
                className="h-40 w-40 object-cover rounded-lg shadow-md"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = ImageNotAvailable;
                }}
              />
            ) : (
              <div className="h-40 w-40 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                <div className="text-center text-gray-500 text-xs">
                  {t.categories.fields.imageUrl}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              {t.buttons.cancel}
            </Button>
          )}
          <Button
            type="submit"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? t.categories.actions.creating
              : isEdit
                ? 'Actualizar Categoría'
                : t.categories.actions.create
            }
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CategoryForm;
